import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "./ui";
import { PaneField, PaneHead, PaneNote, SETTINGS_INPUT, Switch } from "./settings-parts";
import {
  integrationsSeed,
  rolesSeed,
  workspaceAppsSeed,
  workspaceIndustries,
  workspaceLanguages,
  workspaceSizes,
  workspaceZones,
} from "@/mocks/settings";
import { useWorkspaceSettings, type SettingsPatch } from "./workspace-settings";
import { useTeams } from "./teams-store";
import { apiErrorText } from "@/lib/api";
import { useAuth } from "./auth";
import { fieldsApi, type ContactFieldDef, type FieldType } from "./contacts-api";
import { fieldTypes, typeOf, visOf } from "./fields-meta";
import { ZohoSyncPanel, useZohoSync } from "./zoho-sync";
import { goAnchor } from "./app-nav";
import { t, tn } from "@/lib/i18n";

type PaneProps = { onFlash: (text: string) => void };

const SaveButton = ({ onClick, label = t("Сохранить изменения"), disabled }: { onClick: () => void; label?: string; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex h-9 w-fit cursor-pointer disabled:cursor-default disabled:opacity-60 items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
  >
    <Check className="size-3.5" strokeWidth={3} aria-hidden />
    {label}
  </button>
);

/** Сохранение настроек пространства из вкладки: тост об успехе или понятная ошибка сервера */
function useSettingsSaver(onFlash: (text: string) => void) {
  const ws = useWorkspaceSettings();
  const [busy, setBusy] = useState(false);
  const save = async (patch: SettingsPatch, text: string) => {
    setBusy(true);
    try {
      await ws.save(patch);
      onFlash(text);
    } catch (err) {
      onFlash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };
  return { data: ws.data, busy: busy || !ws.data, readOnly: Boolean(ws.data && !ws.data.canEdit), save };
}

/* ── Основные настройки ── */

export function BasicPane({ onFlash }: PaneProps) {
  const ws = useSettingsSaver(onFlash);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("hub");
  const [language, setLanguage] = useState("Русский");
  const [zone, setZone] = useState("Asia/Dubai");
  useEffect(() => {
    if (!ws.data) return;
    setName(ws.data.name);
    setSlug(ws.data.slug);
    setLanguage(ws.data.language);
    setZone(ws.data.timezone);
  }, [ws.data]);
  const slugOk = /^[a-z0-9-]{3,40}$/.test(slug);

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-settings-3-line"
        title={t("Основные настройки")}
        text={t("Название, адрес и язык рабочего пространства — то, что видно в письмах, ссылках и уведомлениях команде.")}
      />

      <div className="mt-4 grid max-w-xl gap-4">
        <PaneField label={t("Название рабочего пространства")} hint={t("До 60 символов. Видно в письмах клиентам и в уведомлениях.")}>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t("Например, отдел продаж")} className={SETTINGS_INPUT} />
        </PaneField>

        <PaneField label={t("Адрес рабочего пространства")} hint={slugOk ? t("Ссылка: hub.example/{slug}", { slug }) : t("Только строчные латинские буквы, цифры и дефис — от 3 до 40 символов.")}>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-[11.5px] text-muted-foreground">hub.example/</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40))}
              aria-invalid={!slugOk}
              className={cn(SETTINGS_INPUT, !slugOk && "border-red-500/70")}
            />
          </div>
        </PaneField>

        <div className="grid gap-4 sm:grid-cols-2">
          <PaneField label={t("Язык интерфейса")}>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className={cn(SETTINGS_INPUT, "cursor-pointer")}>
              {workspaceLanguages.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </PaneField>
          <PaneField label={t("Часовой пояс")}>
            <select value={zone} onChange={(e) => setZone(e.target.value)} className={cn(SETTINGS_INPUT, "cursor-pointer")}>
              {workspaceZones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </PaneField>
        </div>
      </div>

      <div className="mt-5">
        <SaveButton
          disabled={ws.busy || ws.readOnly}
          onClick={() =>
            !name.trim() || !slugOk
              ? onFlash(t("Проверьте название и адрес — без них не сохранить"))
              : void ws.save({ name: name.trim(), slug, language, timezone: zone }, t("Основные настройки сохранены"))
          }
        />
      </div>

      <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-2">
        <div className="rounded-btn border border-border bg-background/40 p-2.5">
          <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
            <i className="ri-link text-[13px] leading-none text-primary" aria-hidden />
            {t("Одна ссылка для команды")}
          </p>
          <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{t("Ссылку на рабочее пространство можно отправить сотрудникам — они войдут по приглашению.")}</p>
        </div>
        <div className="rounded-btn border border-border bg-background/40 p-2.5">
          <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
            <i className="ri-time-line text-[13px] leading-none text-primary" aria-hidden />
            {t("Время в диалогах")}
          </p>
          <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{t("По выбранному поясу показываются время сообщений, рабочие часы и дата в отчётах.")}</p>
        </div>
      </div>

      <PaneNote>{ws.readOnly ? t("Менять настройки рабочего пространства могут владелец и администраторы.") : t("Настройки общие для всего рабочего пространства. Язык интерфейса пока только русский — выбор сохраняется на будущее.")}</PaneNote>
    </div>
  );
}

/* ── Общая информация ── */

export function GeneralPane({ onFlash }: PaneProps) {
  const ws = useSettingsSaver(onFlash);
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (!ws.data) return;
    setDescription(ws.data.description);
    setWebsite(ws.data.website);
    setIndustry(ws.data.industry);
    setSize(ws.data.companySize);
    setEmail(ws.data.notifyEmail);
  }, [ws.data]);

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-information-line"
        title={t("Общая информация")}
        text={t("Описание компании, сайт и отрасль — по этим данным AI-ассистент отвечает клиентам точнее, а отчёты понятнее команде.")}
      />

      <div className="mt-4 grid max-w-xl gap-4">
        <PaneField label={t("Описание компании")} hint={t("{n} / 500 символов. Коротко: чем занимаетесь и кому продаёте.", { n: description.length })}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            rows={3}
            maxLength={500}
            className={cn(SETTINGS_INPUT, "h-auto min-h-[72px] resize-y py-2")}
          />
        </PaneField>

        <div className="grid gap-4 sm:grid-cols-2">
          <PaneField label={t("Сайт")}>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.ru" className={SETTINGS_INPUT} />
          </PaneField>
          <PaneField label={t("Почта для уведомлений")}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="team@example.ru" className={SETTINGS_INPUT} />
          </PaneField>
          <PaneField label={t("Отрасль")}>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} className={cn(SETTINGS_INPUT, "cursor-pointer")}>
              <option value="">{t("Не выбрано")}</option>
              {workspaceIndustries.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </PaneField>
          <PaneField label={t("Размер компании")}>
            <select value={size} onChange={(e) => setSize(e.target.value)} className={cn(SETTINGS_INPUT, "cursor-pointer")}>
              <option value="">{t("Не выбрано")}</option>
              {workspaceSizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </PaneField>
        </div>
      </div>

      <div className="mt-5">
        <SaveButton
          disabled={ws.busy || ws.readOnly}
          onClick={() =>
            void ws.save(
              { description: description.trim(), website: website.trim(), industry, companySize: size, notifyEmail: email.trim() },
              t("Общая информация сохранена — AI-ассистент учтёт её в ответах"),
            )
          }
        />
      </div>
      <PaneNote>{ws.readOnly ? t("Менять общую информацию могут владелец и администраторы.") : t("По этим данным AI-ассистент будет отвечать клиентам (этап 6).")}</PaneNote>
    </div>
  );
}

/* ── Роли пользователей ── */

export function RolesPane({ onFlash }: PaneProps) {
  const [roles, setRoles] = useState(rolesSeed);

  const toggle = (roleId: string, permId: string) =>
    setRoles((prev) =>
      prev.map((r) => (r.id === roleId ? { ...r, permissions: r.permissions.map((p) => (p.id === permId ? { ...p, on: !p.on } : p)) } : r)),
    );

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-shield-user-line"
        title={t("Настройки ролей пользователей")}
        text={t("Роль определяет, что сотрудник видит и может менять в рабочем пространстве. Включите нужные права — они применятся ко всем пользователям этой роли.")}
      />

      <div className="mt-4 flex flex-col gap-2.5">
        {roles.map((r) => {
          const on = r.permissions.filter((p) => p.on).length;
          return (
            <div key={r.id} className="rounded-btn border border-border bg-background/40 p-3">
              <div className="flex flex-wrap items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className={cn(r.icon, "text-base leading-none")} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold leading-snug">
                    {r.name}
                    <span className="rounded-full bg-foreground/[.06] px-1.5 py-[1px] text-[9.5px] font-semibold text-muted-foreground">
                      {tn(r.users, "пользователь", "пользователя", "пользователей")}
                    </span>
                  </p>
                  <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{r.description}</p>
                </div>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9.5px] font-semibold text-primary">{t("{on} из {total} прав", { on, total: r.permissions.length })}</span>
              </div>

              <div className="mt-2.5 grid gap-1.5 border-t border-border/60 pt-2.5 sm:grid-cols-2">
                {r.permissions.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 rounded-btn px-1 py-0.5">
                    <span className={cn("text-[11.5px]", p.on ? "text-foreground" : "text-muted-foreground")}>{p.label}</span>
                    <Switch
                      checked={p.on}
                      label={t("{perm} для роли {role}", { perm: p.label, role: r.name })}
                      onChange={() => toggle(r.id, p.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onFlash(t("Права ролей сохранены — изменения применятся при следующем входе"))}
          className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
        >
          <Check className="size-3.5" strokeWidth={3} aria-hidden />
          {t("Сохранить роли")}
        </button>
        <button
          type="button"
          onClick={() => {
            setRoles(rolesSeed);
            onFlash(t("Права ролей возвращены к значениям по умолчанию"));
          }}
          className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
        >
          {t("Сбросить")}
        </button>
      </div>
      <PaneNote>{t("Владелец рабочего пространства всегда имеет полный доступ — его права не отключаются.")}</PaneNote>
    </div>
  );
}

/* ── Пользовательские поля ── */

export function FieldsPane({ onFlash }: PaneProps) {
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const [fields, setFields] = useState<ContactFieldDef[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const [query, setQuery] = useState("");
  const [optId, setOptId] = useState<string | null>(null); // поле, у которого правим варианты списка
  const [optDraft, setOptDraft] = useState("");

  useEffect(() => {
    fieldsApi
      .list()
      .then(setFields)
      .catch((err) => onFlash(apiErrorText(err)))
      .finally(() => setLoaded(true));
    // onFlash меняется на каждом рендере родителя — грузим один раз
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? fields.filter((f) => `${f.label} ${f.key}`.toLowerCase().includes(q)) : fields;
  }, [fields, query]);

  const add = async () => {
    const name = label.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      // ID поля сервер делает из названия латиницей
      const f = await fieldsApi.create({ label: name, type });
      setFields((prev) => [...prev, f]);
      setLabel("");
      onFlash(t("Поле «{label}» добавлено в карточку контакта — ID {key}", { label: f.label, key: f.key }));
    } catch (err) {
      onFlash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (f: ContactFieldDef) => {
    try {
      const saved = await fieldsApi.update(f.id, { visibility: f.visibility === "always" ? "hidden" : "always" });
      setFields((prev) => prev.map((x) => (x.id === f.id ? saved : x)));
    } catch (err) {
      onFlash(apiErrorText(err));
    }
  };

  const editOptions = (f: ContactFieldDef) => {
    setOptId(f.id);
    setOptDraft(f.options.join(", "));
  };

  const saveOptions = async (f: ContactFieldDef) => {
    const options = Array.from(new Set(optDraft.split(/[,;\n]/).map((o) => o.trim()).filter(Boolean)));
    try {
      const saved = await fieldsApi.update(f.id, { options });
      setFields((prev) => prev.map((x) => (x.id === f.id ? saved : x)));
      setOptId(null);
      onFlash(t("Варианты поля «{label}» сохранены", { label: f.label }));
    } catch (err) {
      onFlash(apiErrorText(err));
    }
  };

  const remove = async (f: ContactFieldDef) => {
    if (f.system) {
      onFlash(t("Встроенное поле «{label}» удалить нельзя — его можно только скрыть", { label: f.label }));
      return;
    }
    if (!window.confirm(t("Удалить поле «{label}»? Его значения будут стёрты из всех карточек контактов.", { label: f.label }))) return;
    try {
      await fieldsApi.remove(f.id);
      setFields((prev) => prev.filter((x) => x.id !== f.id));
      onFlash(t("Поле «{label}» удалено из карточки контакта", { label: f.label }));
    } catch (err) {
      onFlash(apiErrorText(err));
    }
  };

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-input-field"
        title={t("Пользовательские настройки полей")}
        text={t("Соберите карточку клиента под свой отдел продаж: город, номер договора, сегмент и любые другие поля. Скрытые поля не показываются в карточке.")}
      />

      {canEdit ? (
        <div className="mt-4 flex flex-col gap-2 rounded-btn border border-border bg-background/40 p-2.5 sm:flex-row sm:items-end">
          <label className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-[10.5px] font-medium text-muted-foreground">{t("Название поля")}</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void add();
              }}
              placeholder={t("Например, номер договора")}
              maxLength={60}
              className={SETTINGS_INPUT}
            />
          </label>
          <label className="flex w-full flex-col gap-1 sm:w-40">
            <span className="text-[10.5px] font-medium text-muted-foreground">{t("Тип")}</span>
            <select value={type} onChange={(e) => setType(e.target.value as FieldType)} className={cn(SETTINGS_INPUT, "cursor-pointer")}>
              {fieldTypes.map((ft) => (
                <option key={ft.id} value={ft.id}>
                  {ft.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void add()}
            disabled={!label.trim() || busy}
            className="inline-flex h-9 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-add-line text-sm leading-none" aria-hidden />
            {t("Добавить поле")}
          </button>
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-56">
          <i className="ri-search-line pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-xs leading-none text-muted-foreground" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Поиск по полям")}
            aria-label={t("Поиск по полям")}
            className={cn(SETTINGS_INPUT, "ps-7 text-[11.5px]")}
          />
        </div>
        <span className="ms-auto whitespace-nowrap text-[10.5px] text-muted-foreground">
          {loaded ? t("Показано {n} из {total}", { n: visible.filter((f) => f.visibility === "always").length, total: fields.length }) : t("Загружаем поля…")}
        </span>
      </div>

      <ul className="mt-2 divide-y divide-border rounded-btn border border-border">
        {loaded && visible.length === 0 ? (
          <li className="px-3 py-6 text-center text-[11.5px] text-muted-foreground">{t("Поле не найдено — измените запрос.")}</li>
        ) : (
          visible.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-2.5 px-3 py-2">
              <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                <i className="ri-input-field text-[13px] leading-none" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold leading-snug">
                  {f.label}
                  {f.system ? <span className="rounded-full bg-foreground/[.06] px-1.5 py-[1px] text-[9px] font-semibold text-muted-foreground">{t("системное")}</span> : null}
                </p>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {f.key} · {typeOf(f.type).label}
                </p>
              </div>
              <span className="hidden whitespace-nowrap text-[10px] text-muted-foreground sm:inline">{visOf(f.visibility).label}</span>
              {canEdit ? (
                <>
                  <Switch checked={f.visibility === "always"} label={t("Показывать поле {label}", { label: f.label })} onChange={() => void toggle(f)} />
                  <button
                    type="button"
                    onClick={() => void remove(f)}
                    aria-label={t("Удалить поле {label}", { label: f.label })}
                    title={t("Удалить поле")}
                    className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-red-500/10 hover:text-red-500"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                </>
              ) : null}
              {f.type === "multi" ? (
                <div className="basis-full ps-9">
                  {optId === f.id ? (
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
                      <input
                        value={optDraft}
                        onChange={(e) => setOptDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void saveOptions(f);
                          if (e.key === "Escape") setOptId(null);
                        }}
                        placeholder={t("Через запятую: опт, розница, дилер")}
                        aria-label={t("Варианты поля {label}", { label: f.label })}
                        autoFocus
                        className={cn(SETTINGS_INPUT, "h-8 text-[11.5px]")}
                      />
                      <div className="flex flex-none gap-1.5">
                        <button
                          type="button"
                          onClick={() => void saveOptions(f)}
                          className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                        >
                          {t("Сохранить")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setOptId(null)}
                          className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                        >
                          {t("Отмена")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-muted-foreground">
                      <span className="font-semibold">{t("Варианты")}:</span>
                      {f.options.length ? (
                        f.options.map((o) => (
                          <span key={o} className="rounded-full bg-foreground/[.06] px-1.5 py-[1px] text-[10px] font-medium text-foreground/80">
                            {o}
                          </span>
                        ))
                      ) : (
                        <span>{t("не заданы — в карточке можно вводить любые значения")}</span>
                      )}
                      {canEdit ? (
                        <button type="button" onClick={() => editOptions(f)} className="cursor-pointer font-semibold text-primary hover:underline">
                          {f.options.length ? t("Изменить варианты") : t("Задать варианты")}
                        </button>
                      ) : null}
                    </p>
                  )}
                </div>
              ) : null}
            </li>
          ))
        )}
      </ul>
      <PaneNote>
        {canEdit
          ? t("Изменения сохраняются сразу. Варианты списка — по ссылке «Задать варианты» у поля; тип, описание и видимость «Только админ» — в разделе «Поля контактов».")
          : t("У вас только просмотр: поля меняют владелец и администраторы.")}
      </PaneNote>
    </div>
  );
}

/* ── Команды ── */

export function TeamPane({ onFlash }: PaneProps) {
  const { teams, people, create, remove, nameOf, canManage } = useTeams();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await create({ name: n, description: description.trim(), members: picked });
      setName("");
      setDescription("");
      setPicked([]);
      setOpen(false);
      onFlash(t("Команда «{name}» создана — можно назначать ей диалоги и контакты", { name: n }));
    } catch (err) {
      onFlash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const drop = async (id: string, teamName: string) => {
    try {
      await remove(id);
      onFlash(t("Команда «{name}» удалена", { name: teamName }));
    } catch (err) {
      onFlash(apiErrorText(err));
    }
  };

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-team-line"
        title={t("Настройки команды")}
        text={t("Команды группируют сотрудников, чтобы назначать диалоги и контакты группе целиком, а не каждому по отдельности.")}
      />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => (canManage ? setOpen((v) => !v) : onFlash(t("Команды создают владелец, администраторы и менеджеры")))}
          className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
        >
          <i className="ri-add-line text-sm leading-none" aria-hidden />
          {t("Добавить команду")}
        </button>
        <span className="ms-auto whitespace-nowrap text-[10.5px] text-muted-foreground">{t("Команд: {n}", { n: teams.length })}</span>
      </div>

      {open ? (
        <div className="mt-3 grid gap-2 rounded-btn border border-primary/30 bg-primary/[.05] p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] font-medium text-muted-foreground">{t("Название команды")}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder={t("Например, ночная смена")} className={SETTINGS_INPUT} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] font-medium text-muted-foreground">{t("Описание")}</span>
              <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={90} placeholder={t("Чем занимается команда")} className={SETTINGS_INPUT} />
            </label>
          </div>
          <div>
            <span className="text-[10.5px] font-medium text-muted-foreground">{t("Участники")}</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {people.map((m) => {
                const on = picked.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    title={m.email}
                    onClick={() => setPicked((prev) => (on ? prev.filter((x) => x !== m.id) : [...prev, m.id]))}
                    aria-pressed={on}
                    className={cn(
                      "inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10.5px] font-semibold transition",
                      on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {on ? <Check className="size-2.5" strokeWidth={3} aria-hidden /> : null}
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!name.trim() || busy}
              className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("Создать команду")}
            </button>
            <span className="text-[10px] text-muted-foreground">{t("Выбрано участников: {n}", { n: picked.length })}</span>
          </div>
        </div>
      ) : null}

      <ul className="mt-3 flex flex-col gap-2">
        {teams.length === 0 ? (
          <li className="rounded-btn border border-dashed border-border bg-background/40 px-4 py-8 text-center text-[11.5px] text-muted-foreground">
            {t("Пока нет ни одной команды — создайте первую, чтобы группировать сотрудников.")}
          </li>
        ) : (
          teams.map((team) => (
            <li key={team.id} className="flex flex-wrap items-start gap-2.5 rounded-btn border border-border bg-background/40 p-3 transition hover:border-primary/40">
              <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                <i className="ri-team-line text-sm leading-none" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold leading-snug">
                  {team.name}
                  <span className="rounded-full bg-primary/10 px-1.5 py-[1px] text-[9.5px] font-semibold text-primary">{tn(team.members.length, "участник", "участника", "участников")}</span>
                </p>
                <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{team.description || t("Без описания")}</p>
                {team.members.length > 0 ? (
                  <p className="mt-1 truncate text-[10px] text-muted-foreground">{team.members.map(nameOf).join(", ")}</p>
                ) : null}
              </div>
              <button
                type="button"
                hidden={!canManage}
                onClick={() => void drop(team.id, team.name)}
                className="inline-flex h-7 flex-none cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[10.5px] font-semibold text-muted-foreground transition hover:border-red-400/60 hover:text-red-500"
              >
                {t("Удалить")}
              </button>
            </li>
          ))
        )}
      </ul>
      <PaneNote>{t("Роль пользователя при добавлении в команду не меняется: команда только упрощает назначение.")}</PaneNote>
    </div>
  );
}

/* ── Приложения ── */

export function AppsPane({ onFlash }: PaneProps) {
  const [apps, setApps] = useState(workspaceAppsSeed);
  const enabledCount = apps.filter((a) => a.enabled).length;

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-apps-line"
        title={t("Приложения")}
        text={t("Дополнительные приложения рабочего пространства. Включайте нужные — они появятся в меню у всей команды.")}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[10.5px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">
          <Check className="size-2.5" strokeWidth={3} aria-hidden />
          {t("Включено: {n}", { n: enabledCount })}
        </span>
        <span>{t("Всего приложений: {n}", { n: apps.length })}</span>
      </div>

      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {apps.map((a) => (
          <div
            key={a.id}
            className={cn(
              "flex flex-col gap-2 rounded-btn border p-3 transition",
              a.enabled ? "border-primary/40 bg-primary/[.05]" : "border-border bg-background/40 hover:border-primary/40",
            )}
          >
            <div className="flex items-start gap-2.5">
              <span
                className={cn(
                  "grid size-8 flex-none place-items-center rounded-btn text-sm leading-none",
                  a.enabled ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                )}
                aria-hidden
              >
                <i className={a.icon} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold leading-snug">{a.name}</p>
                <p className="mt-0.5 text-[9.5px] uppercase tracking-wider text-muted-foreground">{a.category}</p>
              </div>
              <Switch
                checked={a.enabled}
                label={t("Приложение {name}", { name: a.name })}
                onChange={() => {
                  const next = !a.enabled;
                  setApps((prev) => prev.map((x) => (x.id === a.id ? { ...x, enabled: next } : x)));
                  onFlash(next ? t("Приложение «{name}» включено и появилось в меню", { name: a.name }) : t("Приложение «{name}» выключено", { name: a.name }));
                }}
              />
            </div>
            <p className="text-[10.5px] leading-relaxed text-muted-foreground">{a.description}</p>
          </div>
        ))}
      </div>
      <PaneNote>{t("Демо-режим: включение приложения живёт только на этой странице.")}</PaneNote>
    </div>
  );
}

/* ── Zoho CRM и Books ── */

export function ZohoPane({ onFlash }: PaneProps) {
  // onFlash меняется на каждом рендере родителя — опрос состояния должен зависеть от стабильной функции
  const flashRef = useRef(onFlash);
  flashRef.current = onFlash;
  const flash = useCallback((text: string) => flashRef.current(text), []);
  const z = useZohoSync(flash);

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-refresh-line"
        title={t("Zoho CRM и Books")}
        text={t("Только чтение: компании и клиенты Books, история переписки Wazzup и распределение диалогов по продавцам Zoho (поле Sales Person) между сотрудниками Hub.")}
      />
      <div className="mt-4">
        <ZohoSyncPanel z={z} hideHead />
      </div>
      <div className="mt-3">
        <button
          type="button"
          onClick={() => goAnchor("#zoho-link")}
          className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
        >
          <i className="ri-links-line text-[13px] leading-none" aria-hidden />
          {t("Открыть сопоставление клиентов Books")}
        </button>
      </div>
      <PaneNote>{t("Ключи Zoho задаются в настройках сервера; здесь — запуск синхронизации, докачка истории Wazzup и привязка продавцов к сотрудникам.")}</PaneNote>
    </div>
  );
}

/* ── Интеграции ── */

export function IntegrationsPane({ onFlash }: PaneProps) {
  const [items, setItems] = useState(integrationsSeed);
  const connected = items.filter((i) => i.connected).length;

  return (
    <div className="min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-links-line"
        title={t("Интеграции")}
        text={t("Свяжите Hub с CRM, таблицами и складом — контакты, сделки и остатки будут обновляться синхронно, без ручного переноса.")}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[10.5px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-600">
          <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
          {t("Подключено: {n}", { n: connected })}
        </span>
        <span>{t("Доступно: {n}", { n: items.length })}</span>
      </div>

      <ul className="mt-3 flex flex-col gap-2">
        {items.map((it) => (
          <li
            key={it.id}
            className={cn(
              "flex flex-wrap items-center gap-2.5 rounded-btn border p-3 transition",
              it.connected ? "border-primary/40 bg-primary/[.04]" : "border-border bg-background/40 hover:border-primary/40",
            )}
          >
            <span
              className={cn(
                "grid size-9 flex-none place-items-center rounded-btn text-base leading-none",
                it.connected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
              )}
              aria-hidden
            >
              <i className={it.icon} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold leading-snug">
                {it.name}
                {it.connected ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-1.5 py-[1px] text-[9.5px] font-semibold text-primary">
                    <Check className="size-2.5" strokeWidth={3} aria-hidden />
                    {t("Подключено")}
                  </span>
                ) : null}
              </p>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{it.description}</p>
              {it.connected && it.account ? <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{it.account}</p> : null}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = !it.connected;
                setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, connected: next, account: next ? "sales@hub.ru" : "" } : x)));
                onFlash(next ? t("Интеграция «{name}» подключена", { name: it.name }) : t("Интеграция «{name}» отключена, данные остались в Hub", { name: it.name }));
              }}
              className={cn(
                "inline-flex h-7 flex-none cursor-pointer items-center whitespace-nowrap rounded-btn px-2.5 text-[10.5px] font-semibold transition",
                it.connected
                  ? "border border-border text-muted-foreground hover:border-red-400/60 hover:text-red-500"
                  : "bg-primary text-primary-foreground hover:brightness-110",
              )}
            >
              {it.connected ? t("Отключить") : t("Подключить")}
            </button>
          </li>
        ))}
      </ul>
      <PaneNote>{t("Демо-режим: подключение имитируется в макете и не затрагивает реальные сервисы.")}</PaneNote>
    </div>
  );
}